import type { HanziText } from "@/data/model";
import { and, eq, inArray, not, useLiveQuery } from "@tanstack/react-db";
import { arrayFilterUnique } from "@pinyinly/lib/collections";
import { View } from "@/client/ui/View";
import { CompactWordRows } from "./CompactWordRows";
import type { CompactWordRowsEntry } from "./CompactWordRows";
import { useDb } from "./hooks/useDb";
import { WikiTitledBox } from "./WikiTitledBox";

const maxSamePronunciationEntries = 5;

export function WikiHanziSamePronunciation({ hanzi }: { hanzi: HanziText }) {
  const db = useDb();

  const { data: currentEntries } = useLiveQuery({
    query: (q) =>
      q
        .from({ entry: db.dictionaryCollection })
        .where(({ entry }) => eq(entry.hanzi, hanzi))
        .orderBy(({ entry }) => entry.freq, {
          direction: `desc`,
          nulls: `last`,
        }),
  });

  const primaryPinyin = currentEntries[0]?.pinyin?.[0];

  const { data: matchingEntries } = useLiveQuery({
    query: (q) => {
      if (primaryPinyin == null) {
        return null;
      }

      return q
        .from({ entry: db.dictionaryCollection })
        .where(({ entry }) =>
          and(
            not(eq(entry.hanzi, hanzi)),
            inArray(primaryPinyin, entry.pinyin),
          ),
        )
        .orderBy(({ entry }) => entry.freq, {
          direction: `desc`,
          nulls: `last`,
        });
    },
  });

  if (primaryPinyin == null) {
    return null;
  }

  const samePronunciationRows: CompactWordRowsEntry[] = (matchingEntries ?? [])
    .filter(arrayFilterUnique((entry) => entry.hanziWord))
    .slice(0, maxSamePronunciationEntries)
    .map((entry) => ({
      hanzi: entry.hanzi,
      hanziWord: entry.hanziWord,
      hsk: entry.hsk ?? null,
      hskFirstAppearance: entry.hskFirstAppearance ?? null,
      gloss: entry.gloss,
      pinyin: entry.pinyin ?? null,
    }));

  if (samePronunciationRows.length === 0) {
    return null;
  }

  return (
    <WikiTitledBox title="Same pronunciation">
      <View className="p-3">
        <CompactWordRows dictionaryEntries={samePronunciationRows} />
      </View>
    </WikiTitledBox>
  );
}
